import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { Image, Play } from 'lucide-react';
import { isDynamicSection, renderDynamicSection, loadDynamicSections } from '../utils/sectionRegistry';

export default function ThankYou() {
  const { form_id } = useParams();
  const [searchParams] = useSearchParams();
  const responseId = searchParams.get('response_id');
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load dynamic sections first, then fetch form
    const initializePage = async () => {
      try {
        // Load dynamic sections before fetching form data
        await loadDynamicSections();
      } catch (error) {
        console.error('Failed to load dynamic sections:', error);
      }
      
      // Now fetch the form - this ensures dynamic sections are available when rendering
      await fetchForm(form_id);
    };

    initializePage();
  }, [form_id]);

  const fetchForm = async (form_uuid) => {
    try {
      setLoading(true);
      const response = await apiRequest(`/forms/public/${form_uuid}`);
      if (response.ok) {
        const data = await response.json();
        setForm(data);
      } else {
        setError('Form not found');
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      setError('Failed to load thank you page');
    } finally {
      setLoading(false);
    }
  };

  const renderCustomSection = (section) => {
    const content = section.content;
    
    // Helper function to create comprehensive spacing styles
    const getSpacingStyles = (baseStyles) => ({
      ...baseStyles,
      marginTop: `${content.marginTop || 0}px`,
      marginBottom: `${content.marginBottom || baseStyles.marginBottom || 24}px`,
      paddingTop: `${content.paddingTop || 0}px`,
      paddingBottom: `${content.paddingBottom || 0}px`,
      paddingLeft: `${content.paddingLeft || 0}px`,
      paddingRight: `${content.paddingRight || 0}px`,
    });

    // Simple wrapper for content alignment
    const contentWrapper = (children) => (
      <div style={{ textAlign: content.textAlign || 'center' }}>
        {children}
      </div>
    );
    
    switch (section.section_type) {
      case 'header':
        return contentWrapper(
          <h1 style={getSpacingStyles({
            fontSize: `${content.fontSize || 48}px`,
            fontWeight: content.fontWeight || 'bold',
            textAlign: 'inherit',
            color: content.color || '#1f2937',
            margin: 0,
            marginBottom: '24px'
          })}>
            {content.text || 'Header Text'}
          </h1>
        );
      
      case 'paragraph':
        return contentWrapper(
          <p style={getSpacingStyles({
            fontSize: `${content.fontSize || 18}px`,
            fontWeight: content.fontWeight || 'normal',
            textAlign: 'inherit',
            color: content.color || '#6b7280',
            margin: 0,
            lineHeight: 1.6,
            marginBottom: '16px'
          })}>
            {content.text || 'Paragraph text'}
          </p>
        );
      
      case 'image':
        return content.src ? contentWrapper(
          <img
            src={content.src}
            alt={content.alt || ''}
            style={getSpacingStyles({
              width: content.width === 'auto' ? 'auto' : `${content.width}px`,
              height: content.height === 'auto' ? 'auto' : `${content.height}px`,
              borderRadius: `${content.borderRadius || 8}px`,
              maxWidth: '100%',
              display: 'block',
              margin: '0 auto',
              marginBottom: '24px'
            })}
          />
        ) : contentWrapper(
          <div 
            style={getSpacingStyles({
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '24px'
            })}
          >
            <Image className="w-12 h-12 mx-auto mb-2" />
            <p>No image selected</p>
          </div>
        );
      
      case 'video':
        return content.src ? contentWrapper(
          <iframe
            src={content.src}
            width={content.width || 560}
            height={content.height || 315}
            style={getSpacingStyles({
              borderRadius: `${content.borderRadius || 8}px`,
              maxWidth: '100%',
              display: 'block',
              margin: '0 auto',
              marginBottom: '24px'
            })}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Thank you page video"
          />
        ) : contentWrapper(
          <div 
            style={getSpacingStyles({
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '24px'
            })}
          >
            <Play className="w-12 h-12 mx-auto mb-2" />
            <p>No video URL provided</p>
          </div>
        );
      
      case 'cta':
        // Helper function to get button size styles
        const getButtonSizeStyles = () => {
          const buttonSize = content.buttonSize || 'medium';
          const sizeMap = {
            small: { fontSize: '14px', padding: '8px 16px' },
            medium: { fontSize: '16px', padding: '12px 24px' },
            large: { fontSize: '18px', padding: '16px 32px' }
          };
          return sizeMap[buttonSize] || sizeMap.medium;
        };

        const getButtonWidthStyles = () => {
          const buttonWidth = content.buttonWidth || 'auto';
          return {
            width: buttonWidth === 'full' ? '100%' : 'auto',
            display: buttonWidth === 'full' ? 'block' : 'inline-block'
          };
        };

        const buttonSizeStyles = getButtonSizeStyles();
        const buttonWidthStyles = getButtonWidthStyles();

        return contentWrapper(
          <div style={getSpacingStyles({ 
            marginBottom: '16px'
          })}>
            <a
              href={content.url || '#'}
              target={content.url ? '_blank' : '_self'}
              rel={content.url ? 'noopener noreferrer' : undefined}
              style={{
                backgroundColor: content.backgroundColor || '#4f46e5',
                color: content.textColor || '#ffffff',
                borderRadius: `${content.borderRadius || 8}px`,
                padding: content.padding ? content.padding.split(' ').map(p => `${p}px`).join(' ') : buttonSizeStyles.padding,
                fontSize: content.fontSize ? `${content.fontSize}px` : buttonSizeStyles.fontSize,
                fontWeight: content.fontWeight || 'medium',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'none',
                textAlign: 'center',
                ...buttonWidthStyles
              }}
              onClick={(e) => {
                if (!content.url) {
                  e.preventDefault();
                }
              }}
            >
              {content.text || 'Button Text'}
            </a>
          </div>
        );
      
      default:
        // Check if this is a dynamic section
        if (isDynamicSection(section.section_type)) {
          return renderDynamicSection(section.section_type, content, getSpacingStyles);
        }
        
        return <div className="text-gray-500">Unknown section type: {section.section_type}</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Get the first active thank you page customizer
  const thankYouCustomizer = form?.thank_you_customizers?.[0];
  
  if (!thankYouCustomizer) {
    // Default thank you page if no customizer is set up
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#f9fafb' }}
      >
        <div className="text-center max-w-2xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Thank You!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Your response has been recorded successfully.
          </p>
          {responseId && (
            <p className="text-sm text-gray-500">
              Reference ID: {responseId}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Custom thank you page
  const pageBackgroundColor = thankYouCustomizer.config?.page_background_color || '#f9fafb';

  // Helper function to get section container styles based on page settings
  const getSectionContainerStyles = () => {
    const config = thankYouCustomizer.config || {};
    const { section_width, custom_section_width, section_max_width } = config;
    
    if (section_width === 'full-width') {
      return {
        width: '100%',
        maxWidth: 'none',
        margin: '0',
        padding: '0 1.5rem' // Keep some horizontal padding
      };
    } else if (section_width === 'custom') {
      return {
        width: custom_section_width || '800px',
        maxWidth: section_max_width || '1200px',
        margin: '0 auto',
        padding: '0 1.5rem'
      };
    } else {
      // Default boxed layout
      return {
        width: '100%',
        maxWidth: '1024px', // 4xl equivalent
        margin: '0 auto',
        padding: '0 1.5rem'
      };
    }
  };

  const containerStyles = getSectionContainerStyles();

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: pageBackgroundColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div className="min-h-screen flex flex-col">
        {thankYouCustomizer.sections && thankYouCustomizer.sections.length > 0 ? (
          // Render custom sections with proper full-width handling
          <div>
            {thankYouCustomizer.sections
              .sort((a, b) => a.order_index - b.order_index)
              .map((section) => {
                const content = section.content;
                const hasBackground = content.sectionBackgroundColor && content.sectionBackgroundColor !== 'transparent';
                
                if (hasBackground) {
                  // Full-width section with background
                  return (
                    <div 
                      key={section.id}
                      style={{ backgroundColor: content.sectionBackgroundColor }}
                    >
                      <div style={{...containerStyles, padding: `1.5rem ${containerStyles.padding.split(' ')[1]}`}}>
                        {renderCustomSection(section)}
                      </div>
                    </div>
                  );
                } else {
                  // Regular content-width section
                  return (
                    <div key={section.id} style={containerStyles}>
                      {renderCustomSection(section)}
                    </div>
                  );
                }
              })}
          </div>
        ) : (
          // Fallback to default content with custom styling
          <div style={{...containerStyles, paddingTop: '3rem', paddingBottom: '3rem'}}>
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {thankYouCustomizer.config?.page_title || 'Thank You!'}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Your response has been recorded successfully.
              </p>
              {responseId && (
                <p className="text-sm text-gray-500">
                  Reference ID: {responseId}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}